/**
Author - Brijesh Pandey
Git - https://github.com/brijesh-pandey
**/

import React, {useState, useEffect, useRef} from "react";
import PropTypes from "prop-types";
import {partnerLoginSignupWithCaptcha, getPartnerSignupApiKey} from "api";
import useRecaptcha from "hooks/useRecaptcha";

import {
  WealthyEnv,
  WealthyValidations,
  triggerToast,
  handleFuryApiError,
  getRSessionId,
  handleAuthError
} from "helpers";

import UserCredentials from "./UserCredentials";
import {validateWebSignInOtp} from "api";
import {reSendExistingSignInOtp} from "api";

const inputTypeEnums = {
  phone: "phone",
  email: "email"
};

const propTypes = {
  formData: PropTypes.object,
  inputType: PropTypes.string,
  isLoading: PropTypes.bool,
  rCode: PropTypes.string,
  resetPin: PropTypes.bool,
  setFormData: PropTypes.func,
  setInputType: PropTypes.func,
  setIsLoading: PropTypes.func,
  setRsessionId: PropTypes.func,
  setToggleModal: PropTypes.func,
  setUserData: PropTypes.func,
  setUserStep: PropTypes.func,
  toggleModal: PropTypes.bool,
  userData: PropTypes.object,
  userStep: PropTypes.number
};

const UserCredentialsContainer = ({
  userData,
  setUserData,
  setUserStep,
  setRsessionId,
  setIsLoading,
  rCode,
  resetPin,
  toggleModal,
  setToggleModal,
  isLoading,
  userStep,
  formData,
  setFormData,
  inputType,
  setInputType
}) => {
  const {token, refreshToken} = useRecaptcha("login");
  const [selectedCountryCode, setSelectedCountryCode] = useState({
    country: "India",
    code: "(+91)"
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const inputRefs = useRef([]);
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  useEffect(() => {
    if (userData) {
      if (userData.phoneNumber) {
        setFormData({phoneNumber: userData.phoneNumber});
        setInputType(inputTypeEnums.phone);
      } else if (userData.email) {
        setFormData({email: userData.email, password: ""});
        setInputType(inputTypeEnums.email);
      }
    }
  }, [userData, setFormData, setInputType]);

  const handleInput = (e) => {
    const {name, value} = e.target;

    if (inputType === "phone") {
      setFormData({phoneNumber: value});
    } else if (inputType === "email") {
      if (name === "email") {
        setFormData(prev => ({
          email: value,
          password: prev.password || ""
        }));
      } else if (name === "password") {
        setFormData(prev => ({
          email: prev.email,
          password: value
        }));
      }
    } else {
      setFormData({phoneNumber: value});
    }
  };

  const handleInputTypeChange = (type) => {
    setInputType(type);
    if (type === inputTypeEnums.phone) {
      setFormData({phoneNumber: ""});
    } else if (type === inputTypeEnums.email) {
      setFormData({email: "", password: ""});
    } else {
      setFormData({phoneNumber: ""});
    }
  };

  const validateInput = () => {
    if (inputType === "phone") {
      const phoneCheck = WealthyValidations.validateFormField.internationalPhone(formData?.phoneNumber || "");
      if (!phoneCheck) {
        triggerToast("Enter valid phone number!", "error");
        return false;
      }
    } else if (inputType === "email") {
      if (!formData.password) {
        triggerToast("Please enter your password!", "error");
        return false;
      }
      const emailCheck = WealthyValidations.validateFormField.email(formData.email || "");
      if (!emailCheck) {
        triggerToast("Please enter a valid email address!", "error");
        return false;
      }
    } else {
      triggerToast("Please enter your phone number or email!", "error");
      return false;
    }
    return true;
  };

  const handleCountryCodeChange = (countryData) => {
    setSelectedCountryCode(countryData);
    setShowDropdown(false);
  };

  const handleOtpChange = (index, value) => {
    value = value.replace(/\D/g, "");
    if (value.length > 1) {
      value = value.slice(-1);
    }
    const newOtp = [...otp];
    newOtp[index] = value;
    while (newOtp.length < 5) newOtp.push("");
    if (newOtp.length > 5) newOtp.length = 5;
    setOtp(newOtp);
    if (value && index < 4) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const verifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length === 5) {
      setIsLoading(true);
      try {
        const payload = {
          otp: otpValue,
          leadId: userData.leadId,
          phoneNumber: userData?.phoneNumber
        };
        const res = await validateWebSignInOtp(payload);
        if (res.status === 200) {
          getApiKey(res.data.authCode);
        }
      } catch (err) {
        handleAuthError(err);
        setIsLoading(false);
      }
    }
  };

  const handleResend = async () => {
    setIsResendingOtp(true);
    try {
      await reSendExistingSignInOtp({
        phoneNumber: selectedCountryCode?.code + formData.phoneNumber
      });
    } catch (err) {
      handleAuthError(err);
      setIsLoading(false);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const getApiKey = async (authCode) => {
    try {
      const payload = {
        clientId: `${WealthyEnv.ADVISOR_CLIENT_ID}`,
        authCode
      };
      const res = await getPartnerSignupApiKey(payload);
      const apiKey = res.data?.accessToken;
      if (apiKey?.length > 0 && res.status === 200) {
        redirectToPartnerSystem(apiKey);
        console.log("loading still ot not", isLoading);
        // setToggleModal(false);
      }
    } catch (error) {
      handleAuthError(error);
      setUserStep(1);
      setUserData(null);
      setIsLoading(false);
    }
  };

  const redirectToPartnerSystem = (apiKey) => {
    const url = `${WealthyEnv.BUILDWEALTH_API_URL}/dashboards/api/v0/mobile-redirect/?token=${apiKey}&url=${WealthyEnv.BUILDWEALTH_APP_URL}&app_version=client-web`;
    window.location.href = url;
  };

  const handleCredentials = async () => {
    const isValidInput = validateInput();
    if (!isValidInput) return;

    let captchaToken = token;
    if (!captchaToken) {
      captchaToken = await refreshToken();
    }

    if (!captchaToken) {
      triggerToast("Captcha Failed! Please refresh the page and try again");
      setIsLoading(false);
      return;
    }

    try {
      const rSessionId = getRSessionId();
      setIsLoading(true);
      setRsessionId(rSessionId);
      const payload = {
        ...formData,
        ...(inputType === inputTypeEnums.phone ? {phoneNumber: selectedCountryCode?.code + formData.phoneNumber} : {}),
        accessType: "login"
      };

      const res = await partnerLoginSignupWithCaptcha(payload, captchaToken);
      if (res.status === 200) {
        if (inputType === inputTypeEnums.phone) {
          setUserStep(2);
          setUserData(res.data);
          setIsLoading(false);
        } else if (inputType === inputTypeEnums.email) {
          getApiKey(res.data.authCode);
        }
      }
    } catch (err) {
      handleAuthError(err);
      setUserStep(1);
      setUserData(null);
      setIsLoading(false);
    }
  };

  return (
    <UserCredentials
      formData={formData}
      handleCountryCodeChange={handleCountryCodeChange}
      handleCredentials={handleCredentials}
      handleInput={handleInput}
      handleInputTypeChange={handleInputTypeChange}
      handleKeyDown={handleKeyDown}
      handleOtpChange={handleOtpChange}
      handleResend={handleResend}
      inputRefs={inputRefs}
      inputType={inputType}
      inputTypeEnums={inputTypeEnums}
      isLoading={isLoading}
      otp={otp}
      resetPin={resetPin}
      selectedCountryCode={selectedCountryCode}
      setOtp={setOtp}
      setSelectedCountryCode={setSelectedCountryCode}
      setShowDropdown={setShowDropdown}
      showDropdown={showDropdown}
      userData={userData}
      userStep={userStep}
      verifyOtp={verifyOtp}
      isResendingOtp={isResendingOtp}
      setUserStep={setUserStep}
    />
  );
};

UserCredentialsContainer.propTypes = propTypes;

export default UserCredentialsContainer;
